/**
 * Strip the model-facing scaffolding from a web result snippet.
 *
 * Search snippets are assembled for the model, not the reader: citation
 * processing prepends `<!-- url: … -->\n[N] ` (citation_utils), and highlight
 * excerpts are joined with `\n[…]\n` (WebSearchResultItem.from_exa_result).
 * The preview shows human prose only — no headers, no citation keys, no URLs.
 */
export const cleanSnippet = (raw: string): string =>
  raw
    .replace(/<!--\s*url:[^>]*-->/g, ' ')
    .replace(/^\s*\[\d+\]\s*/, '')
    // Highlight-fragment joiners (ours and Exa's) become a quiet inline break.
    .replace(/\s*\[…\]\s*/g, ' … ')
    .replace(/\s*\n\s*\.\.\.\s*\n\s*/g, ' … ')
    // Markdown links keep their text, never their URL.
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Strip model-facing scaffolding from a research answer while keeping its
 * Markdown links and paragraph structure for the full answer renderer.
 * Inline `[n]` citation markers stay, but line-leading source-block prefixes
 * go.
 */
export const cleanAnswerMarkdown = (raw: string): string =>
  raw
    .replace(/<!--\s*url:[^>]*-->[^\S\n]*\n?/g, '')
    // Multi-citation answers prefix a run of markers ("[1] [2] ..."), so the
    // strip must consume the whole run, not just the first marker.
    .replace(/^(?:[^\S\n]*\[\d+\])+[^\S\n]*/gm, '')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

/** cleanAnswerMarkdown for plain-text surfaces: link labels stay, URLs go. */
export const cleanAnswer = (raw: string): string =>
  cleanAnswerMarkdown(raw).replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')

/**
 * Clean a citation-card preview for muted markdown rendering: same
 * scaffolding strip as cleanAnswer, but images are dropped entirely first —
 * the link-to-text pass would otherwise leave a stray "!alt" behind, and the
 * hover card never renders images anyway.
 */
export const cleanPreviewMarkdown = (raw: string): string =>
  cleanAnswer(raw.replace(/!\[[^\]]*\]\([^)]*\)/g, ''))
